import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { CenteredLoading } from '@tbitop/ui';
import type { PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { toast as toaster } from 'react-hot-toast';

type AppUpdateCheckerProps = {
  appVersionApiUrl: string;
};

const AppUpdateChecker = ({ children, appVersionApiUrl }: PropsWithChildren<AppUpdateCheckerProps>) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isDownloadingNewVersion, setIsDownloadingNewVersion] = useState(false);
  const [newMajorVersionAvailable, setNewMajorVersionAvailable] = useState(false);

  useEffect(() => {
    const checkForFrontendUpdate = async () => {
      try {
        const current = await CapacitorUpdater.current();
        const targetVersionRequest = await fetch(appVersionApiUrl);
        const targetVersionResponse = await targetVersionRequest.json();

        const hasNewMajorVersion = checkForMajorVersionDifference(current.bundle.version, targetVersionResponse.version);
        if (hasNewMajorVersion) {
          setNewMajorVersionAvailable(true);
          return;
        }

        if (current.bundle.version !== targetVersionResponse.version && targetVersionResponse.url) {
          setIsDownloadingNewVersion(true);
          CapacitorUpdater.addListener('downloadComplete', async d => {
            try {
              await CapacitorUpdater.set({ id: d.bundle.id });
            } catch (e) {
              console.error('Updater: Error while setting version', e);
              setIsDownloadingNewVersion(false);
              setIsChecking(false);
              CapacitorUpdater.removeAllListeners();
            }
          });
          CapacitorUpdater.addListener('downloadFailed', async d => {
            const isTestOrDev = appVersionApiUrl.includes('team-beverage.dev');
            toaster.error(`Error while checking for updates ${isTestOrDev ? JSON.stringify(d) : ''}`);
            console.error('Updater: Error while downloading version', d);
            setIsDownloadingNewVersion(false);
            setIsChecking(false);
            CapacitorUpdater.removeAllListeners();
          });
          await CapacitorUpdater.download({
            url: targetVersionResponse.url,
            version: targetVersionResponse.version,
          });
        } else {
          setIsChecking(false);
        }

      } catch (e) {
        toaster.error('Error while checking for updates');
        console.error('Updater: Error while downloading version', e);
        setIsChecking(false);
      }
    };
    checkForFrontendUpdate();
  }, [appVersionApiUrl]);

  function checkForMajorVersionDifference(currentVersion: string, targetVersion: string) {
    console.log('Current version:', currentVersion);
    console.log('Target version:', targetVersion);
    if (!currentVersion || currentVersion === 'builtin') {
      return false;
    }
    try {
      const currentMajorVersion = currentVersion.split('.')[0];
      const targetMajorVersion = targetVersion.split('.')[0];
      return currentMajorVersion !== targetMajorVersion;
    } catch (e) {
      console.error('Error while comparing major versions', { currentVersion, targetVersion });
    }
    return false;
  }

  if (newMajorVersionAvailable) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full p-4">
        <p className="mt-2 text-sm text-center text-gray-500">
          A new version of the app is available. Please download the latest version from the app store.
        </p>
      </div>
    );
  }

  if (isChecking) {
    return <CenteredLoading text={isDownloadingNewVersion ? 'Updating app...' : undefined} />;
  }

  return children;
};

export default AppUpdateChecker;
